import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import queryString from 'query-string';
import { masterData, schedules, tickets } from '../api';

export const VEHICLE_ID_STORAGE_KEY = 'deliveriesVehicleId';

export const isTicketedAssignment = assignment => Boolean(assignment?.ticket?.ticketRef || assignment?.ticket?.id);

export const filterUnticketedAssignments = assignments =>
  (assignments || []).filter(assignment => !isTicketedAssignment(assignment));

const SCHEDULED_LOAD_STATUS_ACTIVE = 'ACTIVE';
const SCHEDULED_LOAD_STATUS_CANCELLED = 'CANCELLED';

const normalizeStatusCode = statusCode => String(statusCode ?? '').toUpperCase();

export const assignmentStatusCode = assignment => assignment?.supplierStatus?.statusCode ?? assignment?.statusCode;

// Drivers act on a load before dispatch moves it past ACTIVE, so ACTIVE and an absent status both read as pending.
export const isPendingStatusCode = statusCode =>
  !statusCode || normalizeStatusCode(statusCode) === SCHEDULED_LOAD_STATUS_ACTIVE;

export const isCancelledAssignment = assignment =>
  normalizeStatusCode(assignmentStatusCode(assignment)) === SCHEDULED_LOAD_STATUS_CANCELLED;

export const filterDisplayableAssignments = assignments =>
  filterUnticketedAssignments(assignments).filter(assignment => !isCancelledAssignment(assignment));

const formatDriverName = vehicle => {
  const firstName = vehicle?.configuration?.driver?.firstName?.trim() || '';
  const lastName = vehicle?.configuration?.driver?.lastName?.trim() || '';
  return [firstName, lastName].filter(Boolean).join(' ');
};

const notFoundVehicle = () => ({
  found: false,
  vehicleRef: null,
  driverRef: null,
  driverName: '',
  locationRef: null,
  carrierRef: null,
  matchesLoggedInDriver: false,
});

export const resolveVehicleForLoadAssignments = async (entityRef, vehicleId) => {
  // MT proxies to setup via get-vehicles — do not call setup from the frontend.
  // includeDriverMatch opts into ids filter + driver match; ticket flows must not send it.
  const vehicleList = await masterData.getMasterData({
    entityRef,
    typeId: 'vehicle',
    truckNumber: vehicleId,
    includeDriverMatch: true,
  });

  if (!vehicleList?.length) {
    return notFoundVehicle();
  }

  const vehicle = vehicleList.find(item => item?.id === vehicleId || String(item?.id) === String(vehicleId));

  if (!vehicle) {
    return notFoundVehicle();
  }

  return {
    found: true,
    vehicleRef: vehicle?.crn || null,
    driverRef: vehicle?.configuration?.driver?.driverRef || null,
    driverName: formatDriverName(vehicle),
    // Ticket creation needs the plant and carrier the truck belongs to; the assignment list does not carry them.
    locationRef: vehicle?.homeLocation?.locationRef || null,
    carrierRef: vehicle?.carrierParty?.carrierRef || null,
    matchesLoggedInDriver: Boolean(vehicle?.matchesLoggedInDriver),
  };
};

export const loadAssignmentsVehicleQueryKey = (entityRef, vehicleId) => [
  'load-assignments-vehicle',
  entityRef,
  vehicleId,
];

export const useVehicleForLoadAssignments = vehicleId => {
  const { entityRef } = useParams();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: loadAssignmentsVehicleQueryKey(entityRef, vehicleId),
    queryFn: () => resolveVehicleForLoadAssignments(entityRef, vehicleId),
    enabled: Boolean(entityRef && vehicleId),
    // Misses can become hits after setup creates the vehicle — always recheck on remount/save.
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
  });

  const refreshVehicle = async nextVehicleId => {
    const id = nextVehicleId ?? vehicleId;
    if (!entityRef || !id) {
      return;
    }
    await queryClient.invalidateQueries({ queryKey: loadAssignmentsVehicleQueryKey(entityRef, id) });
  };

  return {
    ...query,
    refreshVehicle,
  };
};

export const useListScheduledLoadAssignments = (vehicleRef, queryParams = {}) => {
  const { entityRef } = useParams();

  return useQuery({
    queryKey: ['scheduled-load-assignments', entityRef, vehicleRef, queryString.stringify(queryParams)],
    queryFn: () =>
      schedules.listScheduledLoadAssignments({
        entityRef,
        queryParams: {
          ...queryParams,
          vehicleRef,
          allowAllAggregateOrders: true,
        },
      }),
    enabled: Boolean(entityRef && vehicleRef),
    refetchInterval: 60000,
    refetchOnWindowFocus: false,
  });
};

export const useCreateTicketForLoad = () => {
  const { entityRef } = useParams();

  const mutation = useMutation({
    mutationFn: ({ orderRef, loadNumber, payload }) =>
      tickets.createTicketForLoad({
        entityRef,
        orderRef,
        loadNumber,
        payload,
      }),
  });

  return mutation;
};
